import type { Board } from "../board";
import type { Direction } from "../direction";
import { PRIORITY_LAST, type Bumpable, type ProcessableThing, type SomethingWithState } from "../interfaces";
import { DestroyableObstacle } from "../destroyableObstacle";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { PhysicalThing } from "../physicalThing";
import { Player } from "../player";
import { deserializeXY } from "../serialization";
import type { XY } from "../xy";

export enum WoodenDoorState {
	OPEN = "OPEN",
	CLOSED = "CLOSED",
}

export class WoodenDoor extends DestroyableObstacle implements Bumpable, ProcessableThing, SomethingWithState {
	static key = "WoodenDoor";
	private state: WoodenDoorState = WoodenDoorState.CLOSED;
	private wasOccupied: boolean = false;

	constructor(xy: XY) {
		super(xy);
	}

	getDebrisParticleType(): ParticleType {
		return ParticleType.WOOD_DEBRIS;
	}

	bump(_direction: Direction, _author: PhysicalThing): boolean {
		if (this.state === WoodenDoorState.CLOSED) {
			this.state = WoodenDoorState.OPEN;
			this.blocksMovement = false;
			this.wasOccupied = false;
			return true;
		}
		return false;
	}

	processTurn(board: Board): void {
		if (this.state !== WoodenDoorState.OPEN) return;
		const things = board.getThingsAt(this.xy);
		const hasOccupant = things.some((t) => t instanceof Player || t instanceof Mob);
		if (hasOccupant) {
			this.wasOccupied = true;
			return;
		}
		if (!this.wasOccupied) return;
		this.state = WoodenDoorState.CLOSED;
		this.blocksMovement = true;
		this.wasOccupied = false;
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	isHittable(): boolean {
		return this.state === WoodenDoorState.CLOSED;
	}

	getState(): WoodenDoorState {
		return this.state;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			state: this.state,
			wasOccupied: this.wasOccupied,
		};
	}

	static deserialize(data: any): WoodenDoor {
		const d = new WoodenDoor(deserializeXY(data.xy));
		d.restoreBaseFields(data);
		d.state = data.state ?? WoodenDoorState.CLOSED;
		d.wasOccupied = data.wasOccupied ?? false;
		if (d.state === WoodenDoorState.OPEN) {
			d.blocksMovement = false;
		}
		return d;
	}
}
